import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { http } from "../../lib/http";
import PageChrome from "../../components/shell/PageChrome";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, FileText, CheckCircle2, Lock, MessageSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

const TERMS = ["T1", "T2", "T3", "final"];
const MOCK_TYPES = [
  { value: "trinity_gese_1", label: "Trinity GESE 1" },
  { value: "trinity_gese_2", label: "Trinity GESE 2" },
  { value: "trinity_ise_a1", label: "Trinity ISE A1" },
  { value: "trinity_ise_a2", label: "Trinity ISE A2" },
  { value: "trinity_ise_b1", label: "Trinity ISE B1" },
  { value: "trinity_ise_b2", label: "Trinity ISE B2" },
  { value: "trinity_ise_c1", label: "Trinity ISE C1" },
];
const MOCK_RESULTS = [
  { value: "none", label: "—" },
  { value: "pass", label: "Pass" },
  { value: "merit", label: "Merit" },
  { value: "distinction", label: "Distinction" },
  { value: "fail", label: "Fail" },
  { value: "borderline", label: "Borderline" },
  { value: "not_yet", label: "Not Yet" },
];

function ScoreCell({ value, onChange, dirty, disabled }) {
  return (
    <Input
      type="number" min={0} max={10} step={0.5}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      disabled={disabled}
      className={`w-16 h-8 text-center px-1 ${dirty ? "ring-2 ring-amber-400" : ""}`}
    />
  );
}

export default function GradesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [term, setTerm] = useState("T1");
  const [classId, setClassId] = useState("all");
  const [q, setQ] = useState("");
  const [dirtyMap, setDirtyMap] = useState({});  // gradeId -> partial updates
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const isAdmin = user?.role === "admin";
  const canEdit = ["admin", "teacher"].includes(user?.role);

  const fetchData = async () => {
    const qp = new URLSearchParams();
    qp.set("term", term);
    if (classId !== "all") qp.set("class_id", classId);
    const [g, c, s] = await Promise.all([
      http.get(`/grades?${qp.toString()}`),
      http.get(`/classes?status=active&limit=200`),
      http.get(`/students?limit=200`),
    ]);
    setItems(g.data.items || []);
    setClasses(c.data.items || []);
    setStudents(s.data.items || []);
    setDirtyMap({});
  };

  useEffect(() => { fetchData(); }, [term, classId]); // eslint-disable-line

  const studentName = (sid) => {
    const s = students.find((x) => x.id === sid);
    return s ? `${s.first_name} ${s.last_name}` : "—";
  };
  const className = (cid) => classes.find((c) => c.id === cid)?.name || "—";
  const filteredItems = q
    ? items.filter((g) => studentName(g.student_id).toLowerCase().includes(q.toLowerCase()))
    : items;

  const setScore = (gid, key, value) => {
    setItems((arr) => arr.map((g) => g.id === gid ? { ...g, scores: { ...g.scores, [key]: value } } : g));
    setDirtyMap((m) => ({ ...m, [gid]: { ...(m[gid] || {}), scores: { ...((m[gid] && m[gid].scores) || items.find((x) => x.id === gid)?.scores || {}), [key]: value } } }));
  };

  const saveRow = async (g) => {
    const patch = dirtyMap[g.id];
    if (!patch) return;
    try {
      await http.patch(`/grades/${g.id}`, patch);
      toast.success(t("grades.saved"));
      fetchData();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.error === "published_locked" ? t("grades.publishedLocked") : "Save failed");
    }
  };

  const publish = async (g) => {
    if (!window.confirm(t("grades.publishConfirm"))) return;
    try { await http.post(`/grades/${g.id}/publish`); toast.success(t("grades.published")); fetchData(); }
    catch { toast.error("Publish failed"); }
  };

  const removeGrade = async (g) => {
    if (!window.confirm("Delete this draft grade?")) return;
    try { await http.delete(`/grades/${g.id}`); toast.success("Deleted"); fetchData(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const generateStudentReport = async (sid, tt = term) => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("eata.access_token");
      const res = await fetch(`${base}/api/reportcards/student/${sid}?term=${tt}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 409) {
        let count = 0;
        try {
          const d = await res.json();
          count = (d?.detail?.ids || []).length;
        } catch { /* keep count=0 */ }
        toast.error(
          `${t("grades.cannotGenerate")}: ${count} ${t("grades.draftGradesPending")}`,
          {
            description: t("grades.publishFirstHint"),
            action: {
              label: t("grades.showDrafts"),
              onClick: () => { setQ(studentName(sid)); setTerm(tt); },
            },
            duration: 8000,
          },
        );
        return;
      }
      if (res.status === 403) { toast.error(t("grades.notAllowedReport")); return; }
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const j = await res.json(); msg = j?.detail?.error || j?.detail || msg; } catch { /* ignore parse error */ }
        toast.error(`${t("grades.cannotGenerate")}: ${msg}`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(`${t("grades.cannotGenerate")}: network error`);
    }
  };

  return (
    <PageChrome title={t("grades.title")} subtitle={t("grades.subtitle")}>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={term} onValueChange={setTerm}>
          <SelectTrigger className="w-[140px] bg-white" data-testid="grades-filter-term"><SelectValue/></SelectTrigger>
          <SelectContent>{TERMS.map((tt) => <SelectItem key={tt} value={tt}>{tt}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={classId} onValueChange={setClassId}>
          <SelectTrigger className="w-[220px] bg-white" data-testid="grades-filter-class"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")} — {t("classes.title")}</SelectItem>
            {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder={t("grades.searchStudent")} value={q} onChange={(e) => setQ(e.target.value)}
               className="bg-white w-[220px]" data-testid="grades-search"/>
        <div className="ml-auto flex gap-2">
          {canEdit && <Button onClick={() => setCreating(true)} className="bg-[#0A1F44]" data-testid="grades-add-button"><Plus className="h-4 w-4 mr-2"/>{t("grades.add")}</Button>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="p-3">{t("grades.student")}</th>
              <th className="p-3">{t("classes.title")}</th>
              <th className="p-3 text-center">R</th>
              <th className="p-3 text-center">W</th>
              <th className="p-3 text-center">L</th>
              <th className="p-3 text-center">S</th>
              <th className="p-3 text-center">{t("grades.overall")}</th>
              <th className="p-3">{t("grades.status")}</th>
              <th className="p-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-slate-500">{t("common.noResults")}</td></tr>}
            {filteredItems.map((g) => {
              const dirty = !!dirtyMap[g.id];
              const locked = g.status === "published" && !isAdmin;
              return (
                <tr key={g.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`grade-row-${g.id}`}>
                  <td className="p-3 font-medium">{studentName(g.student_id)}</td>
                  <td className="p-3 text-slate-600">{className(g.class_id)}</td>
                  {["reading", "writing", "listening", "speaking"].map((k) => (
                    <td key={k} className="p-2 text-center">
                      <ScoreCell value={(g.scores || {})[k]} onChange={(v) => setScore(g.id, k, v)} dirty={dirty} disabled={locked}/>
                    </td>
                  ))}
                  <td className="p-3 text-center font-bold text-[#0A1F44]">{g.overall ?? "—"}</td>
                  <td className="p-3">
                    <Badge variant={g.status === "published" ? "default" : "secondary"} className={g.status === "published" ? "bg-emerald-600" : ""}>
                      {g.status === "published" && <Lock className="h-2.5 w-2.5 mr-1 inline"/>}
                      {g.status}
                    </Badge>
                  </td>
                  <td className="p-3 text-right space-x-1 whitespace-nowrap">
                    {dirty && <Button size="sm" onClick={() => saveRow(g)} className="bg-amber-500 hover:bg-amber-600" data-testid={`grade-save-${g.id}`}>{t("common.save")}</Button>}
                    <Button variant="ghost" size="icon" onClick={() => setViewing(g)} data-testid={`grade-view-${g.id}`}><MessageSquare className="h-3.5 w-3.5"/></Button>
                    <Button variant="ghost" size="icon" onClick={() => generateStudentReport(g.student_id, g.term)} title="Report card" data-testid={`grade-report-${g.id}`}><FileText className="h-3.5 w-3.5"/></Button>
                    {canEdit && g.status === "draft" && <Button variant="ghost" size="icon" onClick={() => publish(g)} title="Publish" data-testid={`grade-publish-${g.id}`}><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600"/></Button>}
                    {isAdmin && g.status === "draft" && <Button variant="ghost" size="icon" onClick={() => removeGrade(g)} data-testid={`grade-delete-${g.id}`}><Trash2 className="h-3.5 w-3.5 text-[#C8102E]"/></Button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {viewing && (
        <GradeView grade={viewing} onClose={() => setViewing(null)} onUpdated={fetchData}
                   studentName={studentName(viewing.student_id)} className={className(viewing.class_id)} canEdit={canEdit}/>
      )}
      {creating && (
        <GradeCreate classes={classes} students={students} term={term}
                     onClose={() => setCreating(false)} onSaved={() => { setCreating(false); fetchData(); }}/>
      )}
    </PageChrome>
  );
}

function GradeView({ grade, onClose, onUpdated, studentName, className, canEdit }) {
  const { t } = useTranslation();
  const [noteText, setNoteText] = useState("");
  const [comment, setComment] = useState(grade.teacher_comment || "");
  const [mockType, setMockType] = useState(grade.mock_exam?.type || "");
  const [mockResult, setMockResult] = useState(grade.mock_exam?.result || "");

  const saveComment = async () => {
    try {
      await http.patch(`/grades/${grade.id}`, {
        teacher_comment: comment,
        mock_exam: mockType ? { type: mockType, result: mockResult || "not_yet", notes: "" } : null,
      });
      toast.success(t("grades.saved")); onUpdated(); onClose();
    } catch { toast.error("Save failed"); }
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    await http.post(`/grades/${grade.id}/notes`, { text: noteText });
    setNoteText(""); toast.success("Note added"); onUpdated();
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle className="font-serif text-[#0A1F44]" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif" }}>
          {studentName} — {className}
        </DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <div>
            <label className="text-xs uppercase text-slate-500">{t("grades.teacherComment")}</label>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} disabled={!canEdit} data-testid="grade-view-comment"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase text-slate-500">{t("grades.mockType")}</label>
              <Select value={mockType || "none"} onValueChange={(v) => setMockType(v === "none" ? "" : v)} disabled={!canEdit}>
                <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent>{MOCK_TYPES.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase text-slate-500">{t("grades.mockResult")}</label>
              <Select value={mockResult || "none"} onValueChange={(v) => setMockResult(v === "none" ? "" : v)} disabled={!canEdit || !mockType}>
                <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent>{MOCK_RESULTS.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="border-t pt-3">
            <h4 className="text-xs uppercase text-slate-500 mb-2">{t("grades.internalNotes")}</h4>
            <ul className="space-y-1.5 mb-2 max-h-40 overflow-y-auto">
              {(grade.internal_notes || []).map((n, i) => (
                <li key={i} className="text-xs p-2 bg-slate-50 rounded"><b>{(n.created_at || "").slice(0,10)}</b> · {n.text}</li>
              ))}
              {!(grade.internal_notes || []).length && <li className="text-xs text-slate-400">No notes yet.</li>}
            </ul>
            {canEdit && (
              <div className="flex gap-2">
                <Input placeholder="Add private note…" value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="grade-view-note-input"/>
                <Button onClick={addNote} variant="outline" data-testid="grade-view-add-note">Add</Button>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          {canEdit && <Button onClick={saveComment} className="bg-[#0A1F44]">{t("common.save")}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GradeCreate({ classes, students, term, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    student_id: "", class_id: "", term, scores: { reading: null, writing: null, listening: null, speaking: null },
    teacher_comment: "", status: "draft",
  });
  const submit = async () => {
    try { await http.post("/grades", form); toast.success(t("grades.saved")); onSaved(); }
    catch (e) { toast.error("Create failed"); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-serif text-[#0A1F44]" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif" }}>{t("grades.add")}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
            <SelectTrigger data-testid="grade-create-class"><SelectValue placeholder={t("classes.title")}/></SelectTrigger>
            <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
            <SelectTrigger data-testid="grade-create-student"><SelectValue placeholder={t("grades.student")}/></SelectTrigger>
            <SelectContent>
              {students
                .filter((s) => !form.class_id || (classes.find((c) => c.id === form.class_id)?.enrolled_student_ids || []).includes(s.id))
                .map((s) => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="grid grid-cols-4 gap-2">
            {["reading", "writing", "listening", "speaking"].map((k) => (
              <div key={k}>
                <label className="text-xs text-slate-500 uppercase">{k.slice(0,1).toUpperCase()}</label>
                <Input type="number" min={0} max={10} step={0.5}
                       value={form.scores[k] ?? ""}
                       onChange={(e) => setForm({ ...form, scores: { ...form.scores, [k]: e.target.value === "" ? null : Number(e.target.value) }})}/>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={submit} className="bg-[#0A1F44]" data-testid="grade-create-save">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
